import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormGroupDirective, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ErrorCode } from '@common/http/enums/error-code.enum';
import { AuthService } from '@portal-core/auth/services/auth.service';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { FormsService } from '@portal-core/forms/services/forms.service';
import { FormValidationConfig } from '@portal-core/forms/util/form-validation-config';
import { FormValidationContext } from '@portal-core/forms/util/form-validation-context';
import { CancelableEvent } from '@portal-core/general/classes/cancelable-event';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { SnackBarAutoDismissDurationMS } from '@portal-core/ui/snack-bar/constants/snack-bar-auto-dismiss-duration.constant';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { LoadingState } from '@portal-core/util/loading-state';
import { Subscription } from 'rxjs';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { McColorDirective } from '@portal-core/ui/themes/directives/mc-color.directive';
import { MatInput } from '@angular/material/input';
import { FormControlErrorCodeComponent } from '@portal-core/forms/components/form-control-error-code/form-control-error-code.component';
import { NgIf, AsyncPipe } from '@angular/common';
import { ErrorsExpansionPanelComponent } from '@portal-core/general/components/errors-expansion-panel/errors-expansion-panel.component';
import { MatButton } from '@angular/material/button';
import { LoaderComponent } from '@portal-core/general/components/loader/loader.component';

@Component({
    selector: 'mc-user-password-form',
    templateUrl: './user-password-form.component.html',
    styleUrls: ['./user-password-form.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [ReactiveFormsModule, MatFormField, McColorDirective, MatLabel, MatInput, MatError, FormControlErrorCodeComponent, NgIf, ErrorsExpansionPanelComponent, MatButton, LoaderComponent, AsyncPipe]
})
@AutoUnsubscribe()
export class UserPasswordFormComponent implements OnInit {
  private readonly snackBar = inject(MatSnackBar);
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly authService = inject(AuthService);
  private readonly errorService = inject(ErrorService);
  private readonly formsService = inject(FormsService);
  @Input() licenseUser: LicenseUser;
  @Output() canceled: EventEmitter<void> = new EventEmitter<void>();
  @Output() save: EventEmitter<CancelableEvent> = new EventEmitter<CancelableEvent>();
  @Output() saved: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('formDirective', { static: true }) formDirective: FormGroupDirective;

  get dirty(): boolean {
    return this.passwordForm?.dirty;
  }

  savingState: LoadingState<string> = new LoadingState<string>();
  showCancelButton: boolean;
  passwordForm: UntypedFormGroup;
  formValidationConfig: FormValidationConfig;
  formValidationContext: FormValidationContext = new FormValidationContext();
  validationSubscription: Subscription;
  ngOnInit() {
    this.showCancelButton = this.canceled.observers.length > 0;
    this.buildForm();
  }

  onCancelClicked() {
    this.canceled.emit();
  }

  onSubmit(formGroup: UntypedFormGroup) {
    if (!formGroup.valid) {
      return;
    }

    const saveEvent = new CancelableEvent();
    this.save.emit(saveEvent);

    if (!saveEvent.defaultPrevented) {
      this.savingState.update(true);

      this.authService.changePassword$(this.licenseUser.User.Id, formGroup.value['currentPassword'], formGroup.value['newPassword']).subscribe(() => {
        this.savingState.update(false);
        this.formDirective.resetForm();
        this.snackBar.open('Your password has been updated.', 'OK', { duration: SnackBarAutoDismissDurationMS });
        this.saved.emit();
      }, error => {
        this.formsService.updateValidationContext(this.passwordForm, this.formValidationConfig, this.formValidationContext, error);
        this.savingState.update(false, 'Unable to update your password.', this.errorService.getErrorMessages(error));
      });
    }
  }

  protected buildForm() {
    // config for error codes that become validation errors
    this.formValidationConfig = new FormValidationConfig({
      incorrect: { code: ErrorCode.UserIncorrectCredentialsError, useServerMessage: true },
      oldPassword: ErrorCode.UserOldPasswordHistoryError
    }, {
      currentPassword: ['incorrect'],
      newPassword: ['oldPassword']
    });

    this.passwordForm = this.formBuilder.group({
      currentPassword: new UntypedFormControl('', [
        Validators.required,
        ...this.formsService.createErrorCodeValidators(this.formValidationConfig, this.formValidationContext, 'currentPassword')
      ]),
      newPassword: new UntypedFormControl(''),
      confirmPassword: new UntypedFormControl('', [
        Validators.required,
        this.formsService.createMatchValueValidator('newPassword')
      ])
    });

    this.authService.getMinPasswordLength$(this.licenseUser.User.UserName).subscribe(minPasswordLength => {
      this.passwordForm.controls.newPassword.setValidators([
        Validators.required,
        this.formsService.minPasswordLengthValidator(minPasswordLength),
        this.formsService.createMismatchValueValidator('currentPassword'),
        ...this.formsService.createErrorCodeValidators(this.formValidationConfig, this.formValidationContext, 'newPassword')
      ]);
      this.passwordForm.controls.newPassword.updateValueAndValidity();
    });

    this.validationSubscription = this.formsService.initErrorCodeValidation(this.passwordForm, this.formValidationConfig, this.formValidationContext);
  }
}
